import { useEffect, useRef } from "react";

function isDocumentHidden() {
	return typeof document !== "undefined" && document.hidden;
}

class DelayPoller {
	constructor({ getFn, interval, pauseWhenHidden, immediate }) {
		this.getFn = getFn;
		this.interval = interval;
		this.pauseWhenHidden = pauseWhenHidden;
		this.immediate = immediate;
		this.timeoutId = null;
		this.running = false;
		this.stopped = false;
		this.onVisibilityChange = this.onVisibilityChange.bind(this);
	}

	start() {
		if (this.immediate) {
			this.runTick();
		} else {
			this.scheduleNext();
		}
		if (this.pauseWhenHidden && typeof document !== "undefined") {
			document.addEventListener("visibilitychange", this.onVisibilityChange);
		}
	}

	stop() {
		this.stopped = true;
		this.clearPending();
		if (this.pauseWhenHidden && typeof document !== "undefined") {
			document.removeEventListener("visibilitychange", this.onVisibilityChange);
		}
	}

	clearPending() {
		if (this.timeoutId == null) return;
		clearTimeout(this.timeoutId);
		this.timeoutId = null;
	}

	shouldWait() {
		return this.pauseWhenHidden && isDocumentHidden();
	}

	scheduleNext() {
		if (this.stopped || this.shouldWait()) return;
		this.timeoutId = setTimeout(() => this.runTick(), this.interval);
	}

	async runTick() {
		if (this.stopped || this.running || this.shouldWait()) return;

		this.running = true;
		try {
			await this.getFn();
		} catch (error) {
			console.error("Error in polling function", error);
		} finally {
			this.running = false;
		}
		this.scheduleNext();
	}

	onVisibilityChange() {
		if (!this.pauseWhenHidden) return;
		if (isDocumentHidden()) {
			this.clearPending();
			return;
		}
		// Fresh delay on become-visible, not an immediate tick.
		this.scheduleNext();
	}
}

/**
 * Delay-after-complete poller: wait `interval` → run `fn` → wait again after it settles.
 * First tick is delayed by default; pass `immediate: true` to run once on start, then
 * continue with delay-after-complete. Changing `fn` identity does not reset the timer.
 *
 * @param {() => void | Promise<void>} fn
 * @param {{ interval: number, enabled?: boolean, pauseWhenHidden?: boolean, immediate?: boolean }} options
 */
export default function usePolling(fn, { interval, enabled = true, pauseWhenHidden = false, immediate = false } = {}) {
	const fnRef = useRef(fn);
	fnRef.current = fn;

	useEffect(() => {
		const intervalOk = typeof interval === "number" && interval > 0;
		if (!enabled || !intervalOk) return;

		const poller = new DelayPoller({
			getFn: () => fnRef.current(),
			interval,
			pauseWhenHidden,
			immediate,
		});
		poller.start();
		return () => poller.stop();
	}, [enabled, interval, pauseWhenHidden, immediate]);
}
